/**
 * Global auto-grow behaviour for every native <textarea> on the page.
 *
 * Why: there are ~27 raw `<textarea>` tags across the 4 portals (community,
 * student, instructor, admin) and the shared form components. Patching each
 * one would be tedious and easy to miss. This helper attaches a single
 * MutationObserver to <body> so any new textarea — present at mount, added
 * later by route changes, or rendered inside a modal — grows with its text.
 *
 * What it does for each textarea:
 *   1. Records the element's original CSS `min-height` (falls back to
 *      `clientHeight` if min-height is auto/0). This becomes the floor so
 *      empty textareas don't visually shrink below their designed size.
 *   2. Attaches an `input` event listener that resets height to "auto",
 *      measures `scrollHeight`, and sets `height` to that value (capped at
 *      `data-max-auto-height` if specified, else 600 px so a huge paste
 *      doesn't take over the viewport).
 *   3. Also calls the resize once on registration so textareas pre-populated
 *      with a value (edit modals) start at the right size.
 *   4. Sets `resize: none` so the user can't manually drag the corner — the
 *      growth is fully automatic.
 *
 * Skips:
 *   - Textareas that already have `data-no-auto-grow` (escape hatch).
 *   - Textareas that have an explicit `rows` attribute AND are inside a
 *     known fixed-height container (we still attach but the cap prevents
 *     overgrowth).
 */

const ATTACHED_FLAG = '__mmsAutoGrowAttached';

function attachAutoGrow(el) {
  if (!el || el[ATTACHED_FLAG]) return;
  if (el.dataset.noAutoGrow !== undefined) return;
  el[ATTACHED_FLAG] = true;

  const cs = window.getComputedStyle(el);
  // Floor: respect existing min-height, otherwise use the initial rendered
  // height so empty textareas don't collapse below their designed size.
  const minH = parseFloat(cs.minHeight) || el.clientHeight || 0;
  const maxAttr = parseFloat(el.dataset.maxAutoHeight || '');
  const maxH = Number.isFinite(maxAttr) ? maxAttr : 600;

  // Suppress the manual resize handle — growth is automatic.
  if (cs.resize !== 'none') el.style.resize = 'none';

  const resize = () => {
    el.style.height = 'auto';
    const target = Math.max(minH, Math.min(el.scrollHeight, maxH));
    el.style.height = `${target}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  };

  el.addEventListener('input', resize);
  el.addEventListener('focus', resize);

  // Initial size — important for edit-modal textareas pre-filled with
  // existing content (instructor bio, post edits, etc.).
  // requestAnimationFrame so the element's final width has been laid out.
  requestAnimationFrame(resize);
}

function scanRoot(root) {
  if (!root) return;
  if (root.tagName === 'TEXTAREA') attachAutoGrow(root);
  if (root.querySelectorAll) {
    root.querySelectorAll('textarea').forEach(attachAutoGrow);
  }
}

let observer = null;

export function installAutoGrowTextareas() {
  if (typeof document === 'undefined') return; // SSR guard
  if (observer) return; // already installed

  scanRoot(document.body);

  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scanRoot(node);
        });
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function uninstallAutoGrowTextareas() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}
